import { Flex, Stack, Text } from '@mantine/core'
import { IconButton } from '@mui/material'
import AudioFileCard from 'src/components/BookUniVini/AudioFileCard'
import {
	BOOK_UNIVINI_ICON_SECONDARY,
	MagicPenIcon,
} from 'src/components/BookUniVini/BookUniViniFileIcons'
import touchClasses from 'src/components/BookUniVini/BookUniViniTouch.module.scss'
import DocFileCard from 'src/components/BookUniVini/DocFileCard'
import { ChapterAudioDialectVariant, ChapterEditLanguageSection } from 'src/interfaces'
import {
	HIGHLIGHT_STATUS_CONFIG,
	isAudioProcessingStatus,
} from 'src/utils/chapterSlotStatus'

interface LanguageFileSectionProps {
	section: ChapterEditLanguageSection
	onDownloadDoc?: (language: string) => void
	onRegenerateDoc?: (section: ChapterEditLanguageSection) => void
	onRemoveDoc?: (section: ChapterEditLanguageSection) => void
	onRegenerateAudio?: (variant: ChapterAudioDialectVariant) => void
	onRemoveAudio?: (variant: ChapterAudioDialectVariant) => void
	regeneratingAudioId?: string | null
	regeneratingDocId?: string | null
}

function LanguageFileSection({
	section,
	onDownloadDoc,
	onRegenerateDoc,
	onRemoveDoc,
	onRegenerateAudio,
	onRemoveAudio,
	regeneratingAudioId,
	regeneratingDocId,
}: LanguageFileSectionProps) {
	const canManageDoc =
		!section.protectDocActions && Boolean(section.docBookAudioId) && !section.docRemoved
	const isDocRegenerating = regeneratingDocId === section.docBookAudioId
	const isDocProcessing = Boolean(section.docProcessing)

	const showDocBlock =
		canManageDoc || (section.docUrl && !section.docRemoved) || (isDocProcessing && !section.docRemoved)

	if (!section.audioVariants.length && !showDocBlock) {
		return null
	}

	return (
		<Stack spacing={10}>
			<Text size="sm" weight={600} sx={{ color: '#111827' }}>
				{section.label}
			</Text>

			{showDocBlock && (
				<Stack spacing={8}>
					<Flex
						justify="space-between"
						align="center"
						gap={8}
						sx={{
							flexWrap: 'wrap',
							'@media (max-width: 1024px)': {
								alignItems: 'flex-start',
							},
						}}
					>
						<Text size="sm" weight={500} sx={{ color: '#374151' }}>
							Document
						</Text>
						{canManageDoc && onRegenerateDoc && !section.docUrl && !isDocProcessing && (
							<IconButton
								onClick={() => onRegenerateDoc(section)}
								disabled={isDocRegenerating}
								aria-label="Generate document"
								className={touchClasses.touchIconButton}
								sx={{ color: BOOK_UNIVINI_ICON_SECONDARY, p: 0.5 }}
							>
								<MagicPenIcon size={20} />
							</IconButton>
						)}
					</Flex>

					{isDocProcessing && !section.docUrl ? (
						<Text size="xs" color="dimmed" sx={{ fontStyle: 'italic' }}>
							Processing document...
						</Text>
					) : section.docUrl ? (
						<DocFileCard
							url={section.docUrl}
							onDownload={
								onDownloadDoc ? () => onDownloadDoc(section.language) : undefined
							}
							onRegenerate={
								canManageDoc && onRegenerateDoc && !isDocProcessing
									? () => onRegenerateDoc(section)
									: undefined
							}
							onDelete={
								canManageDoc && onRemoveDoc ? () => onRemoveDoc(section) : undefined
							}
							showDelete={canManageDoc && Boolean(onRemoveDoc)}
							regenerating={isDocRegenerating}
						/>
					) : canManageDoc ? (
						<Text size="xs" color="dimmed" sx={{ fontStyle: 'italic' }}>
							No document yet
						</Text>
					) : null}
				</Stack>
			)}

			{section.audioVariants.map((variant) => {
				const isRegenerating = regeneratingAudioId === variant.bookAudioId
				const isProcessing = isAudioProcessingStatus(variant.status)
				const hasAudioFile = Boolean(variant.audio?.url)
				const showHeaderGenerate =
					onRegenerateAudio && !hasAudioFile && !isProcessing
				const highlightConfig =
					HIGHLIGHT_STATUS_CONFIG[variant.highlightStatus ?? 'none']

				return (
					<Stack key={`${variant.bookAudioId}-${variant.language_variant ?? variant.dialectCode}`} spacing={8}>
						<Flex
						justify="space-between"
						align="center"
						gap={8}
						sx={{
							flexWrap: 'wrap',
							'@media (max-width: 1024px)': {
								alignItems: 'flex-start',
							},
						}}
					>
							<Stack spacing={2}>
								<Text size="sm" weight={500} sx={{ color: '#374151' }}>
									{variant.label}
								</Text>
								<Text
									size="xs"
									weight={600}
									sx={{ color: highlightConfig.color }}
								>
									{highlightConfig.label}
								</Text>
							</Stack>
							{showHeaderGenerate && (
								<IconButton
									onClick={() => onRegenerateAudio(variant)}
									disabled={isRegenerating}
									aria-label="Generate audio"
									className={touchClasses.touchIconButton}
									sx={{ color: BOOK_UNIVINI_ICON_SECONDARY, p: 0.5 }}
								>
									<MagicPenIcon size={20} />
								</IconButton>
							)}
						</Flex>

						{isProcessing && !variant.audio?.url ? (
							<Text size="xs" color="dimmed" sx={{ fontStyle: 'italic' }}>
								Processing...
							</Text>
						) : variant.audio ? (
							<AudioFileCard
								audio={variant.audio}
								onRegenerate={
									onRegenerateAudio ? () => onRegenerateAudio(variant) : undefined
								}
								onRemove={onRemoveAudio ? () => onRemoveAudio(variant) : undefined}
								regenerating={isRegenerating}
							/>
						) : (
							<Text size="xs" color="dimmed" sx={{ fontStyle: 'italic' }}>
								No audio yet
							</Text>
						)}
					</Stack>
				)
			})}
		</Stack>
	)
}

export default LanguageFileSection
